import React, {useState} from 'react';
import ProjectsService from '../services/projectsService';

export const ProjectsContext = React.createContext();

const ProjectsContextProvider = ({ children }) => {
  const [pending, setPending] = useState(false);
  const [exportPending, setExportPending] = useState(false);
  const [addProjectPending, setAddProjectPending] = useState(false);
  const [projectStatePending, setProjectStatePending] = useState(false);
  const [error, setError] = useState(false);
  const [pgeProjects, setPgeProjects] = useState([]);
  const [pgePaginationInfo, setPgePaginationInfo] = useState([]);
  const [stoenProjects, setStoenProjects] = useState([]);
  const [stoenPaginationInfo, setStoenPaginationInfo] = useState([]);

  const getPgeProjects = async (size, page, sort, filters, archived) => {
    setError(false);
    setPending(true);
    const responseObj = await ProjectsService.getPgeProjects(size, page, sort, filters, archived);

    if (responseObj.type === 'Error') {
      setError(true);
    } else {
      setPgeProjects(responseObj.content);
      setPgePaginationInfo({
        number: responseObj.number,
        totalPages: responseObj.totalPages,
        totalRecords: responseObj.totalElements
      });
    }

    setPending(false);

    return responseObj;
  };

  const exportPgeProjects = async (size, page, sort, filters, archived) => {
    setError(false);
    setExportPending(true);
    const responseObj = await ProjectsService.exportPgeProjects(size, page, sort, filters, archived);

    if (responseObj.type === 'Error') {
      setError(true);
    }

    setExportPending(false);

    return responseObj;
  };

  const getSinglePgeProject = async id => {
    setError(false);
    setAddProjectPending(true);
    const responseObj = await ProjectsService.getSinglePgeProject(id);

    if (responseObj.type === 'Error') {
      setAddProjectPending(false);
      setError(true);
    } else {
      setAddProjectPending(false);
      return responseObj;
    }
  };

  const getStoenProjects = async (size, page, sort, filters, archived) => {
    setError(false);
    setPending(true);
    const responseObj = await ProjectsService.getStoenProjects(size, page, sort, filters, archived);

    if (responseObj.type === 'Error') {
      setError(true);
    } else {
      setStoenProjects(responseObj.content);
      setStoenPaginationInfo({
        number: responseObj.number,
        totalPages: responseObj.totalPages,
        totalRecords: responseObj.totalElements
      });
    }

    setPending(false);

    return responseObj;
  };

  const exportStoenProjects = async (size, page, sort, filters, archived) => {
    setError(false);
    setExportPending(true);
    const responseObj = await ProjectsService.exportStoenProjects(size, page, sort, filters, archived);

    if (responseObj.type === 'Error') {
      setError(true);
    }

    setExportPending(false);

    return responseObj;
  };

  const getSingleStoenProject = async id => {
    setError(false);
    setAddProjectPending(true);
    const responseObj = await ProjectsService.getSingleStoenProject(id);

    if (responseObj.type === 'Error') {
      setAddProjectPending(false);
      setError(true);
    } else {
      setAddProjectPending(false);
      return responseObj;
    }
  };

  const addPgeProject = async formValues => {
    setError(false);
    setAddProjectPending(true);

    let payload = {...formValues};

    const response = await ProjectsService.addPgeProject(payload);

    if (response !== 200) {
      setError(true);
      setAddProjectPending(false);
      return response;
    }

    setAddProjectPending(false);
    return response;
  };

  const addStoenProject = async formValues => {
    setError(false);
    setAddProjectPending(true);

    let payload = {...formValues};

    const response = await ProjectsService.addStoenProject(payload);

    if (response !== 200) {
      setError(true);
      setAddProjectPending(false);
      return response;
    }

    setAddProjectPending(false);
    return response;
  };

  const updatePgeProject = async (formValues, projectId, version, single) => {
    setError(false);
    setAddProjectPending(true);

    let payload = {...formValues};

    const response = !single
      ? await ProjectsService.updatePgeProject(payload, projectId, version)
      : await ProjectsService.updateSingleFieldInPgeProject(payload, projectId, version);

    if (response !== 200) {
      setError(true);
      setAddProjectPending(false);
      return response;
    }

    setAddProjectPending(false);
    return response;
  };

  const updateStoenProject = async (formValues, projectId, version, single) => {
    setError(false);
    setAddProjectPending(true);

    let payload = {...formValues};

    const response = !single
      ? await ProjectsService.updateStoenProject(payload, projectId, version)
      : await ProjectsService.updateSingleFieldInStoenProject(payload, projectId, version);

    if (response !== 200) {
      setError(true);
      setAddProjectPending(false);
      return response;
    }

    setAddProjectPending(false);
    return response;
  };

  const deletePgeProject = async (projectId) => {
    setError(false);
    setAddProjectPending(true);

    const response = await ProjectsService.deletePgeProject(projectId);

    if (response !== 200) {
      setError(true);
      setAddProjectPending(false);
      return response;
    }

    setAddProjectPending(false);
    return response;
  };

  const deleteStoenProject = async (projectId) => {
    setError(false);
    setAddProjectPending(true);

    const response = await ProjectsService.deleteStoenProject(projectId);

    if (response !== 200) {
      setError(true);
      setAddProjectPending(false);
      return response;
    }

    setAddProjectPending(false);
    return response;
  };

  const updatePgeProjectState = async (newState, projectId, comment) => {
    setError(false);
    setProjectStatePending(true);

    let payload = {newState, projectId, comment};

    const response = await ProjectsService.updatePgeProjectState(payload);

    if (response !== 200) {
      setError(true);
      setProjectStatePending(false);
      return response;
    }

    setProjectStatePending(false);
    return response;
  };

  const updateStoenProjectState = async (newState, projectId, comment) => {
    setError(false);
    setProjectStatePending(true);

    let payload = {newState, projectId, comment};

    const response = await ProjectsService.updateStoenProjectState(payload);

    if (response !== 200) {
      setError(true);
      setProjectStatePending(false);
      return response;
    }

    setProjectStatePending(false);
    return response;
  };

  return (
    <ProjectsContext.Provider
      value={{
        pending,
        exportPending,
        addProjectPending,
        pgePaginationInfo,
        stoenPaginationInfo,
        error,
        pgeProjects,
        stoenProjects,
        projectStatePending,
        getPgeProjects,
        exportPgeProjects,
        getSinglePgeProject,
        getStoenProjects,
        exportStoenProjects,
        getSingleStoenProject,
        addPgeProject,
        addStoenProject,
        updatePgeProject,
        updateStoenProject,
        deletePgeProject,
        deleteStoenProject,
        updatePgeProjectState,
        updateStoenProjectState
      }}
    >
      {children}
    </ProjectsContext.Provider>
  );
};

export default ProjectsContextProvider;
